import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { RouterOutputs } from "@/trpc";
import {
  formatUsageBillingCurrency,
  formatUsageBillingQuantity,
  getUsageEventSourceLabel,
} from "./usage-billing-format";
import { formatUsageBillingDateTime } from "./usage-billing-time";

type UsageBillingEvent =
  RouterOutputs["adminUsageBilling"]["getUnitEvents"]["items"][number];

type UsageBillingEventDetailSheetProps = {
  event: UsageBillingEvent | null;
  onOpenChange: (open: boolean) => void;
};

export function UsageBillingEventDetailSheet({
  event,
  onOpenChange,
}: UsageBillingEventDetailSheetProps) {
  return (
    <Sheet open={Boolean(event)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Usage event
            {event?.voidedAt && <Badge variant="warning">Voided</Badge>}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Full details for this usage event
          </SheetDescription>
        </SheetHeader>
        {event && <UsageBillingEventDetailBody event={event} />}
      </SheetContent>
    </Sheet>
  );
}

type UsageBillingEventDetailBodyProps = {
  event: UsageBillingEvent;
};

function UsageBillingEventDetailBody({
  event,
}: UsageBillingEventDetailBodyProps) {
  const recordedByLabel =
    event.recordedByName || event.recordedByEmail || event.recordedByUserId;

  return (
    <div className="mt-6 space-y-6">
      <dl className="space-y-4">
        <DetailRow label="Category">
          {event.categoryDisplayName}
          <span className="ml-1 text-gray-400">({event.categoryCode})</span>
        </DetailRow>
        <DetailRow label="Occurred at">
          {formatUsageBillingDateTime(event.occurredAt)}
        </DetailRow>
        <DetailRow label="Recorded at">
          {formatUsageBillingDateTime(event.createdAt)}
        </DetailRow>
        <DetailRow label="Quantity">
          {formatUsageBillingQuantity(event.quantity)}
        </DetailRow>
        <DetailRow label="Basis amount">
          {event.basisAmount
            ? formatUsageBillingCurrency(event.basisAmount)
            : "—"}
        </DetailRow>
        <DetailRow label="Source">
          <div className="flex flex-col items-end gap-1">
            <span>{getUsageEventSourceLabel(event)}</span>
            <SourceDetails event={event} />
            <SourceIdSnippet event={event} />
          </div>
        </DetailRow>
        {event.recordedByUserId && (
          <DetailRow label="Recorded by">
            <span className="flex flex-col items-end gap-1">
              <span>{recordedByLabel}</span>
              <Link
                to="/admin/users/$userId"
                params={{ userId: event.recordedByUserId }}
                className="text-blue-600 hover:underline"
              >
                View user
              </Link>
            </span>
          </DetailRow>
        )}
      </dl>

      <div>
        <span className="text-sm text-gray-500">Idempotency key</span>
        <div className="mt-1 break-all rounded bg-gray-50 px-2 py-1 font-mono text-xs">
          {event.idempotencyKey}
        </div>
      </div>

      {event.voidedAt && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <DetailRow label="Voided at">
            {formatUsageBillingDateTime(event.voidedAt)}
          </DetailRow>
          {event.voidedByName && (
            <DetailRow label="Voided by">{event.voidedByName}</DetailRow>
          )}
          {event.voidReason && (
            <DetailRow label="Reason">{event.voidReason}</DetailRow>
          )}
        </div>
      )}
    </div>
  );
}

// Mirrors getUsageEventSourceLabel's precedence: each source type carries
// its own admin-page link and context.
function SourceDetails({ event }: { event: UsageBillingEvent }) {
  if (event.sourceRevealId) {
    return <RevealSourceDetails event={event} />;
  }
  if (event.sourceJobPostId) {
    return (
      <Link
        to="/admin/job-posts/$jobPostId"
        params={{ jobPostId: event.sourceJobPostId }}
        className="text-blue-600 hover:underline"
      >
        View job post
      </Link>
    );
  }
  if (event.sourceFormSubmissionFileResultId) {
    return <SignatureSourceDetails event={event} />;
  }
  if (event.sourceProviderPayableItemId) {
    return <PayableSourceDetails event={event} />;
  }
  return null;
}

function RevealSourceDetails({ event }: { event: UsageBillingEvent }) {
  if (!event.revealedProviderUserId) {
    return null;
  }

  const providerLabel =
    event.revealedProviderName || event.revealedProviderUserId;

  return (
    <>
      <span className="text-gray-900">{providerLabel}</span>
      <Link
        to="/admin/provider-profile/$userId"
        params={{ userId: event.revealedProviderUserId }}
        className="text-blue-600 hover:underline"
      >
        View provider
      </Link>
    </>
  );
}

function PayableSourceDetails({ event }: { event: UsageBillingEvent }) {
  return (
    <>
      {event.payableJobOfferTitle && (
        <span className="text-gray-900">{event.payableJobOfferTitle}</span>
      )}
      {event.payableProviderName && (
        <span className="text-gray-900">{event.payableProviderName}</span>
      )}
      {event.payableProviderUserId && (
        <Link
          to="/admin/provider-profile/$userId"
          params={{ userId: event.payableProviderUserId }}
          className="text-blue-600 hover:underline"
        >
          View provider
        </Link>
      )}
    </>
  );
}

function SignatureSourceDetails({ event }: { event: UsageBillingEvent }) {
  return (
    <>
      {event.signatureFormName && (
        <span className="text-gray-900">{event.signatureFormName}</span>
      )}
      {event.signatureSubmitterName && (
        <span className="text-gray-900">{event.signatureSubmitterName}</span>
      )}
      {event.signatureSubmittedByUserId && (
        <Link
          to="/admin/users/$userId"
          params={{ userId: event.signatureSubmittedByUserId }}
          className="text-blue-600 hover:underline"
        >
          View submitter
        </Link>
      )}
    </>
  );
}

// The raw source id is only meaningful once we know which FK is populated,
// so this mirrors getUsageEventSourceLabel's precedence instead of
// re-deriving it independently.
function SourceIdSnippet({ event }: { event: UsageBillingEvent }) {
  const sourceId =
    event.sourceRevealId ||
    event.sourceJobPostId ||
    event.sourceFormSubmissionFileResultId ||
    event.sourceProviderPayableItemId;

  if (!sourceId) {
    return null;
  }

  return (
    <span className="rounded bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
      {sourceId}
    </span>
  );
}

type DetailRowProps = {
  label: string;
  children: ReactNode;
};

export function DetailRow({ label, children }: DetailRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd className="text-right text-gray-900">{children}</dd>
    </div>
  );
}
